import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  Circle,
  ControlPosition,
  Map as GoogleMap,
  MapControl,
  useMap,
} from '@vis.gl/react-google-maps';
import {
  Box,
  Skeleton
} from '@mui/material';
import { keyframes } from '@mui/system';
import CenterFocusStrongOutlined from '@mui/icons-material/CenterFocusStrongOutlined';
import type { DriveTime } from '../../../types/Supplier';
import FreightPlaceMarkers, {
  FreightPlaceIcon,
  HIGHLIGHT_TIMING,
  NearbyFocusController,
  PulseRing,
  useMapZoom,
} from './FreightPlaceMarkers';

const DEFAULT_CENTER = { lat: 37.09024, lng: -95.712891 };

// The starting view; people may zoom out to see nearby ports and ramps, never closer to the yard.
const AREA_ZOOM = 14;

// The API sends a point up to 0.2 mi from the yard, so the yard is always inside this circle.
const AREA_RADIUS_METERS = 402.336; // 0.25 mi
const AREA_COLOR = '#4DAAE1';

// From here out the circle is under about 25 px, smaller than the port and ramp icons around it.
const PIN_MAX_ZOOM = 12;

// Up, drop, a smaller hop, settle.
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  35% { transform: translateY(-16px); }
  60% { transform: translateY(0); }
  75% { transform: translateY(-6px); }
  90% { transform: translateY(0); }
`;

/** A teardrop map pin in the circle's colour; it bounces while the yard is highlighted. */
const YardPin = ({ bouncing, onBounceEnd }: { bouncing: boolean, onBounceEnd: () => void }) =>
  <Box data-yard-pin data-bounce={bouncing || undefined} onAnimationEnd={onBounceEnd} sx={{
    display: 'block',
    lineHeight: 0,
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35))',
    ...(bouncing && {
      animation: `${bounce} ${HIGHLIGHT_TIMING}`,
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }),
  }}>
    <svg width="28" height="38" viewBox="0 0 28 38" aria-hidden="true">
      <path d="M14 1C6.8 1 1 6.8 1 14c0 9.8 13 23 13 23s13-13.2 13-23C27 6.8 21.2 1 14 1z"
        fill={AREA_COLOR} stroke="#1F7FB6" strokeWidth="1.5" />
      <circle cx="14" cy="14" r="5" fill="#FFFFFF" />
    </svg>
  </Box>;

const CHIP_SX = {
  display: 'flex',
  alignItems: 'center',
  px: 1.5,
  py: 1,
  bgcolor: '#FFFFFF',
  border: '1px solid #E0E0E0',
  borderRadius: '6px',
  boxShadow: '0 1px 3px rgba(50,69,89,0.06), 0 4px 12px rgba(50,69,89,0.06)',
  color: '#324559',
  fontSize: 13,
  fontWeight: 600,
} as const;

const parseCoord = (value?: string | number): number | undefined => {
  const numeric = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(numeric) ? (numeric as number) : undefined;
};

// Without coordinates the map falls back to the whole US and draws no area.
const toArea = (lat?: string | number, lng?: string | number) => {
  const parsedLat = parseCoord(lat);
  const parsedLng = parseCoord(lng);
  return parsedLat === undefined || parsedLng === undefined ? undefined : { lat: parsedLat, lng: parsedLng };
};

interface LocationProfileMapPreviewProps {
  lat?: string | number;
  lng?: string | number;
  loading?: boolean;
  /** The location's Nearby list; those places are drawn larger and their cards show the drive time. */
  driveTimes?: DriveTime[];
  /** A place picked from the Nearby list; the map zooms to show it and the yard. */
  focus?: { name: string, lat: number, lng: number };
}

// Only when the map marks the yard. Worded for the circle and the zoomed-out pin alike.
export const LocationProfileMapCaption = ({ lat, lng }: Pick<LocationProfileMapPreviewProps, 'lat' | 'lng'>) => <>
  {toArea(lat, lng) && 'The map shows the yard\'s approximate location. '}
  The exact address and gate instructions are shared once your booking is confirmed.
</>;

interface YardMarkerProps {
  area: google.maps.LatLngLiteral;
  focus?: { name: string };
  /** The pin is an advanced marker, which needs a map ID; without one the circle shows at every zoom. */
  pinAvailable: boolean;
}

/**
 * The yard on the map: the circle up close, and a pin instead once the circle is too small to find among
 * the port and ramp icons. When a Nearby place is picked the yard is highlighted with it, so both ends
 * show: the pin bounces, or the circle's centre pulses. The pin sits on the circle's centre.
 */
const YardMarker = ({ area, focus, pinAvailable }: YardMarkerProps) => {
  const zoom = useMapZoom();
  // Counts picks so each one restarts the highlight from the beginning; undefined when there is none.
  const [highlightRun, setHighlightRun] = useState<number>();
  const showPin = pinAvailable && zoom <= PIN_MAX_ZOOM;

  useEffect(() => {
    if (focus) {
      setHighlightRun((previous) => (previous ?? 0) + 1);
    }
  }, [focus]);

  return <>
    {!showPin && <Circle center={area} radius={AREA_RADIUS_METERS}
      fillColor={AREA_COLOR} fillOpacity={0.2} strokeColor={AREA_COLOR} strokeWeight={2} clickable={false} />}
    {highlightRun !== undefined && !showPin && pinAvailable && <AdvancedMarker key={highlightRun} position={area}
      anchorPoint={AdvancedMarkerAnchorPoint.CENTER} zIndex={2}>
      <div style={{ position: 'relative', width: '28px', height: '28px' }}>
        <PulseRing color={AREA_COLOR} onEnd={() => setHighlightRun(undefined)} />
      </div>
    </AdvancedMarker>}
    {showPin && <AdvancedMarker position={area} title="Approximate yard area" zIndex={3}>
      <YardPin key={highlightRun ?? 'still'} bouncing={highlightRun !== undefined}
        onBounceEnd={() => setHighlightRun(undefined)} />
    </AdvancedMarker>}
  </>;
};

/** Sits with Google's zoom buttons and returns to the starting view. */
const BackToYardButton = ({ area }: { area: google.maps.LatLngLiteral }) => {
  const map = useMap();
  return <MapControl position={ControlPosition.RIGHT_BOTTOM}>
    <Box component="button" type="button" aria-label="Back to the yard" title="Back to the yard"
      onClick={() => map?.moveCamera({ center: area, zoom: AREA_ZOOM })} sx={{
        width: 40, height: 40, m: '10px', p: 0, border: 0, borderRadius: '2px', bgcolor: '#FFFFFF', color: '#666666',
        boxShadow: 'rgba(0, 0, 0, 0.3) 0 1px 4px -1px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&:hover': { color: '#333333' },
      }}>
      <CenterFocusStrongOutlined aria-hidden="true" />
    </Box>
  </MapControl>;
};

const LocationProfileMapPreview = (props: LocationProfileMapPreviewProps) => {
  const { lat, lng, loading, driveTimes, focus } = props;
  const area = useMemo(() => toArea(lat, lng), [lat, lng]);
  const mapBox = useRef<HTMLDivElement>(null);
  const chipRow = useRef<HTMLDivElement>(null);
  // Port and rail icons are advanced markers, which need a map ID.
  const mapId = import.meta.env.VITE_GOOGLE_MAPS_ID;

  if (loading) {
    return <Skeleton variant="rectangular" width={"100%"} height={359} />;
  }

  // The scroll margin clears the 80 px sticky site header when a Nearby pick scrolls the map into view.
  return <Box ref={mapBox} sx={{ position: 'relative', scrollMarginTop: '96px' }}>
    {/* Keyed on the point so a new location starts again from its own view. */}
    <GoogleMap key={area ? `${area.lat},${area.lng}` : 'us'}
      mapId={mapId}
      defaultCenter={area ?? DEFAULT_CENTER}
      defaultZoom={area ? AREA_ZOOM : 4}
      maxZoom={AREA_ZOOM}
      gestureHandling='cooperative'
      disableDefaultUI
      zoomControl
      clickableIcons={false}
      className='ss-location-profile-map-preview-container'
    >
      {area && <YardMarker area={area} focus={focus} pinAvailable={!!mapId} />}
      <NearbyFocusController area={area} focus={focus} scrollTarget={mapBox} overlay={chipRow} />
      {mapId && <FreightPlaceMarkers driveTimes={driveTimes} focus={focus} />}
      {area && <BackToYardButton area={area} />}
    </GoogleMap>
    {/* One wrapping row so the legend drops under the area chip on a phone instead of covering it. */}
    {area && <Box ref={chipRow} data-map-overlay sx={{
      position: 'absolute', top: 16, left: 16, right: 16, display: 'flex', flexWrap: 'wrap',
      justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, pointerEvents: 'none',
    }}>
      <Box sx={{ ...CHIP_SX, gap: 1 }}>
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.5 2" />
        </svg>
        Approximate area · within 0.25 mi
      </Box>
      {mapId && <Box sx={{ ...CHIP_SX, gap: 1.5 }}>
        {(['PORT', 'RAIL_RAMP'] as const).map((type) => <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <FreightPlaceIcon type={type} size={20} />
          {type === 'PORT' ? 'Port' : 'Rail ramp'}
        </Box>)}
      </Box>}
    </Box>}
  </Box>;
}

export default LocationProfileMapPreview;
